import { useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import {
  ADMIN_PUBKEY,
  useAdminConfig,
  useAdminAnalytics,
  type AdminConfig,
  type NpubEntry,
  type ContentFilter,
  type ListMode,
} from '@/hooks/useAdminConfig';
import { nip19 } from 'nostr-tools';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

// ── Tab types ────────────────────────────────────────────────────────────────

type AdminTab = 'overview' | 'filters' | 'access' | 'analytics';

// ── Icon helpers ─────────────────────────────────────────────────────────────

function ShieldIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4z" />
    </svg>
  );
}
function FilterIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
      <path d="M4.25 5.61C6.27 8.2 10 13 10 13v6c0 .55.45 1 1 1h2c.55 0 1-.45 1-1v-6s3.72-4.8 5.74-7.39A1 1 0 0 0 18.95 4H5.04a1 1 0 0 0-.79 1.61z" />
    </svg>
  );
}
function UsersIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
      <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z" />
    </svg>
  );
}
function AnalyticsIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
      <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-7 14H7v-2h5v2zm5-4H7v-2h10v2zm0-4H7V7h10v2z" />
    </svg>
  );
}
function TrashIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
      <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" />
    </svg>
  );
}
function PlusIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
      <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" />
    </svg>
  );
}
function HomeIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
      <path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z" />
    </svg>
  );
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function toNpub(hex: string): string {
  try { return nip19.npubEncode(hex); } catch { return hex; }
}
function fromNpubOrHex(value: string): string | null {
  value = value.trim();
  if (/^[0-9a-f]{64}$/.test(value)) return value;
  try {
    const d = nip19.decode(value);
    if (d.type === 'npub') return d.data;
  } catch { /* fall through */ }
  return null;
}
function shortKey(hex: string): string {
  const npub = toNpub(hex);
  return npub.slice(0, 12) + '…' + npub.slice(-6);
}
function fmtDate(ts: number): string {
  return new Date(ts * 1000).toLocaleDateString();
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function AdminPage() {
  const { user } = useCurrentUser();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<AdminTab>('overview');

  // Gate: only ADMIN_PUBKEY may access this page
  if (!user) {
    return (
      <AdminShell>
        <div className="flex flex-col items-center justify-center min-h-[60vh] gap-6">
          <ShieldIcon />
          <h2 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>
            Admin Access Required
          </h2>
          <p style={{ color: 'var(--text-secondary)' }}>
            You must be logged in with the admin account to access this area.
          </p>
          <Button onClick={() => navigate('/')} variant="outline">
            Go Home
          </Button>
        </div>
      </AdminShell>
    );
  }

  if (user.pubkey !== ADMIN_PUBKEY) {
    return (
      <AdminShell>
        <div className="flex flex-col items-center justify-center min-h-[60vh] gap-6">
          <div className="p-4 rounded-full" style={{ background: 'rgba(244,67,54,0.15)' }}>
            <svg width="40" height="40" viewBox="0 0 24 24" fill="#f44336">
              <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm-1 13H9l-2-2 1.41-1.41L10 12.17l4.59-4.58L16 9l-5 5z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold" style={{ color: '#f44336' }}>
            Access Denied
          </h2>
          <p style={{ color: 'var(--text-secondary)' }}>
            You do not have permission to access this area.
          </p>
          <Button onClick={() => navigate('/')} variant="outline">
            Go Home
          </Button>
        </div>
      </AdminShell>
    );
  }

  const TABS: { id: AdminTab; label: string; icon: React.ReactNode }[] = [
    { id: 'overview', label: 'Overview', icon: <ShieldIcon /> },
    { id: 'filters', label: 'Content Filters', icon: <FilterIcon /> },
    { id: 'access', label: 'Access Control', icon: <UsersIcon /> },
    { id: 'analytics', label: 'Analytics', icon: <AnalyticsIcon /> },
  ];

  return (
    <AdminShell>
      {/* Header */}
      <div
        className="sticky top-0 z-10 flex items-center gap-4 px-6 py-4 border-b"
        style={{ background: 'var(--bg-secondary)', borderColor: 'var(--gb-border)' }}
      >
        <div className="p-2 rounded-lg" style={{ background: 'rgba(58,158,58,0.15)' }}>
          <ShieldIcon />
        </div>
        <div>
          <h1 className="text-xl font-bold" style={{ color: 'var(--text-primary)' }}>
            Goblinbox Admin
          </h1>
          <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>
            Moderation & Site Management
          </p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <span className="text-xs px-2 py-1 rounded-full" style={{ background: 'rgba(58,158,58,0.15)', color: '#3a9e3a' }}>
            ● Admin
          </span>
          <Link to="/">
            <Button variant="ghost" size="sm" className="gap-1.5">
              <HomeIcon />
              <span className="hidden sm:inline">Back to Site</span>
            </Button>
          </Link>
        </div>
      </div>

      {/* Tab bar */}
      <div
        className="flex overflow-x-auto border-b"
        style={{ borderColor: 'var(--gb-border)', background: 'var(--bg-secondary)' }}
      >
        {TABS.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={cn(
              'flex items-center gap-2 px-5 py-3 text-sm font-medium whitespace-nowrap transition-colors border-b-2',
              activeTab === tab.id
                ? 'border-[#3a9e3a]'
                : 'border-transparent',
            )}
            style={{
              color: activeTab === tab.id ? '#3a9e3a' : 'var(--text-secondary)',
            }}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div className="p-6">
        {activeTab === 'overview' && <OverviewTab />}
        {activeTab === 'filters' && <FiltersTab />}
        {activeTab === 'access' && <AccessTab />}
        {activeTab === 'analytics' && <AnalyticsTab />}
      </div>
    </AdminShell>
  );
}

// ── Admin shell ───────────────────────────────────────────────────────────────

function AdminShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen" style={{ background: 'var(--bg-primary)', color: 'var(--text-primary)' }}>
      <div className="max-w-6xl mx-auto">
        {children}
      </div>
    </div>
  );
}

// ── Search Relays Card ────────────────────────────────────────────────────────

const WELL_KNOWN_SEARCH_RELAYS = [
  { url: 'wss://relay.nostr.band', label: 'relay.nostr.band — full-text search index' },
  { url: 'wss://nostr.wine', label: 'nostr.wine — curated search relay' },
  { url: 'wss://search.nos.today', label: 'search.nos.today — NIP-50 search' },
  { url: 'wss://relay.noswhere.com', label: 'relay.noswhere.com — search relay' },
];

function SearchRelaysCard() {
  const { config, updateConfig } = useAdminConfig();
  const relays: string[] = config.searchRelays ?? [];
  const [input, setInput] = useState('');
  const [error, setError] = useState('');

  const normalise = (raw: string) => {
    raw = raw.trim();
    if (!raw) return null;
    if (!raw.startsWith('wss://') && !raw.startsWith('ws://')) raw = 'wss://' + raw;
    try { new URL(raw); return raw; } catch { return null; }
  };

  const add = (url?: string) => {
    const cleaned = normalise(url ?? input);
    if (!cleaned) { setError('Enter a valid relay URL (wss://…)'); return; }
    if (relays.includes(cleaned)) { setError('Relay already added'); return; }
    setError('');
    updateConfig(c => ({ ...c, searchRelays: [...(c.searchRelays ?? []), cleaned] }));
    setInput('');
  };

  const remove = (url: string) => {
    updateConfig(c => ({ ...c, searchRelays: (c.searchRelays ?? []).filter(r => r !== url) }));
  };

  const clearAll = () => {
    updateConfig(c => ({ ...c, searchRelays: [] }));
  };

  return (
    <div
      className="rounded-xl border p-6 space-y-5"
      style={{ background: 'var(--bg-secondary)', borderColor: 'var(--gb-border)' }}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>
            Search Relays
          </h2>
          <p className="text-sm mt-0.5" style={{ color: 'var(--text-secondary)' }}>
            Dedicated relays used for video and people search queries. Specialised search relays
            (e.g. relay.nostr.band) index far more events and support richer NIP-50 full-text search.
            Leave empty to use the app's default relay pool.
          </p>
        </div>
        {relays.length > 0 && (
          <span
            className="shrink-0 px-2 py-0.5 rounded-full text-xs font-semibold"
            style={{ background: 'rgba(58,158,58,0.15)', color: '#3a9e3a' }}
          >
            {relays.length} active
          </span>
        )}
      </div>

      {/* Quick-add well-known relays */}
      <div>
        <p className="text-xs font-medium mb-2" style={{ color: 'var(--text-secondary)' }}>
          Quick add
        </p>
        <div className="flex flex-wrap gap-2">
          {WELL_KNOWN_SEARCH_RELAYS.map(r => {
            const already = relays.includes(r.url);
            return (
              <button
                key={r.url}
                type="button"
                onClick={() => !already && add(r.url)}
                disabled={already}
                title={r.label}
                className="px-3 py-1.5 rounded-lg text-xs font-medium transition-colors border"
                style={{
                  background: already ? 'rgba(58,158,58,0.12)' : 'var(--bg-primary)',
                  borderColor: already ? '#3a9e3a' : 'var(--gb-border)',
                  color: already ? '#3a9e3a' : 'var(--text-secondary)',
                  cursor: already ? 'default' : 'pointer',
                }}
              >
                {already ? '✓ ' : '+ '}{r.url.replace('wss://', '')}
              </button>
            );
          })}
        </div>
      </div>

      {/* Manual entry */}
      <div className="flex gap-2">
        <Input
          value={input}
          onChange={e => { setInput(e.target.value); setError(''); }}
          onKeyDown={e => e.key === 'Enter' && add()}
          placeholder="wss://your-relay.example.com"
          style={{ background: 'var(--bg-primary)', borderColor: 'var(--gb-border)', color: 'var(--text-primary)' }}
        />
        <Button
          onClick={() => add()}
          style={{ background: '#3a9e3a', color: '#fff', whiteSpace: 'nowrap' }}
          className="gap-1.5 shrink-0"
        >
          <PlusIcon />
          Add
        </Button>
      </div>
      {error && <p className="text-sm" style={{ color: '#f44336' }}>{error}</p>}

      {/* Active relay list */}
      {relays.length === 0 ? (
        <div
          className="rounded-lg px-4 py-6 text-center text-sm border border-dashed"
          style={{ borderColor: 'var(--gb-border)', color: 'var(--text-secondary)' }}
        >
          No custom search relays configured — using the app's default relay pool.
        </div>
      ) : (
        <div
          className="rounded-lg border overflow-hidden"
          style={{ borderColor: 'var(--gb-border)' }}
        >
          <div
            className="flex items-center justify-between px-4 py-2.5 border-b"
            style={{ borderColor: 'var(--gb-border)', background: 'var(--bg-primary)' }}
          >
            <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--text-secondary)' }}>
              Active search relays
            </span>
            <button
              type="button"
              onClick={clearAll}
              className="text-xs hover:underline"
              style={{ color: '#f44336', background: 'none', border: 'none', cursor: 'pointer' }}
            >
              Clear all
            </button>
          </div>
          <ul className="divide-y" style={{ borderColor: 'var(--gb-border)' }}>
            {relays.map(url => (
              <li
                key={url}
                className="flex items-center justify-between px-4 py-2.5 gap-3"
              >
                <div className="flex items-center gap-2 min-w-0">
                  {/* Green pulsing dot */}
                  <span
                    className="shrink-0 w-2 h-2 rounded-full"
                    style={{ background: '#3a9e3a', boxShadow: '0 0 0 2px rgba(58,158,58,0.25)' }}
                  />
                  <code className="text-sm truncate" style={{ color: 'var(--text-primary)' }}>
                    {url}
                  </code>
                </div>
                <button
                  type="button"
                  onClick={() => remove(url)}
                  className="shrink-0 p-1.5 rounded transition-colors"
                  style={{ color: '#f44336' }}
                  title="Remove"
                >
                  <TrashIcon />
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

// ── Overview Tab ──────────────────────────────────────────────────────────────

function OverviewTab() {
  const { config, updateConfig } = useAdminConfig();
  const { analytics } = useAdminAnalytics();

  const totalViews = analytics.pageViews?.length ?? 0;
  const totalUploads = analytics.uploads?.length ?? 0;
  const totalVisitors = analytics.uniqueVisitors?.length ?? 0;

  // Views in last 7 days
  const weekAgo = Math.floor(Date.now() / 1000) - 7 * 86400;
  const weeklyViews = (analytics.pageViews ?? []).filter(t => t > weekAgo).length;

  const stats = [
    { label: 'Total Page Views', value: totalViews.toLocaleString(), accent: '#3a9e3a' },
    { label: 'Weekly Views', value: weeklyViews.toLocaleString(), accent: '#1da1f2' },
    { label: 'Unique Visitors', value: totalVisitors.toLocaleString(), accent: '#9c27b0' },
    { label: 'Total Uploads', value: totalUploads.toLocaleString(), accent: '#f7931a' },
  ];

  return (
    <div className="space-y-8">
      {/* Stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map(s => (
          <div
            key={s.label}
            className="rounded-xl p-5 border"
            style={{ background: 'var(--bg-secondary)', borderColor: 'var(--gb-border)' }}
          >
            <p className="text-sm mb-1" style={{ color: 'var(--text-secondary)' }}>{s.label}</p>
            <p className="text-3xl font-bold" style={{ color: s.accent }}>{s.value}</p>
          </div>
        ))}
      </div>

      {/* Quick settings */}
      <div
        className="rounded-xl border p-6 space-y-5"
        style={{ background: 'var(--bg-secondary)', borderColor: 'var(--gb-border)' }}
      >
        <h2 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>
          Quick Settings
        </h2>

        {/* Maintenance message */}
        <div className="space-y-2">
          <Label style={{ color: 'var(--text-primary)' }}>
            Site-wide Maintenance Message
          </Label>
          <Textarea
            value={config.maintenanceMessage}
            onChange={e => updateConfig(c => ({ ...c, maintenanceMessage: e.target.value }))}
            placeholder="Leave empty to disable. Shows a banner to all users."
            rows={3}
            style={{ background: 'var(--bg-primary)', borderColor: 'var(--gb-border)', color: 'var(--text-primary)' }}
          />
        </div>

        {/* Strict moderation */}
        <div className="flex items-center justify-between py-1">
          <div>
            <p className="font-medium text-sm" style={{ color: 'var(--text-primary)' }}>
              Strict Content Moderation
            </p>
            <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>
              Applies all enabled content filters globally to every feed
            </p>
          </div>
          <Switch
            checked={config.strictModeration}
            onCheckedChange={v => updateConfig(c => ({ ...c, strictModeration: v }))}
          />
        </div>
      </div>

      {/* Search Relays */}
      <SearchRelaysCard />

      {/* Mode summary */}
      <div
        className="rounded-xl border p-6 space-y-4"
        style={{ background: 'var(--bg-secondary)', borderColor: 'var(--gb-border)' }}
      >
        <h2 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>
          Access Mode Summary
        </h2>
        <div className="grid sm:grid-cols-2 gap-4">
          <ModeBadge title="Upload Mode" mode={config.uploadMode} />
          <ModeBadge title="View Mode" mode={config.viewMode} />
        </div>
        <div className="grid sm:grid-cols-4 gap-3 text-center text-sm">
          <CountCard label="Upload Whitelist" count={config.uploadWhitelist.length} />
          <CountCard label="Upload Blacklist" count={config.uploadBlacklist.length} />
          <CountCard label="View Whitelist" count={config.viewWhitelist.length} />
          <CountCard label="View Blacklist" count={config.viewBlacklist.length} />
        </div>
      </div>

      {/* Recent uploads */}
      <div
        className="rounded-xl border p-6 space-y-4"
        style={{ background: 'var(--bg-secondary)', borderColor: 'var(--gb-border)' }}
      >
        <h2 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>
          Recent Uploads
        </h2>
        {analytics.uploads?.length === 0 ? (
          <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
            No uploads recorded yet.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow style={{ borderColor: 'var(--gb-border)' }}>
                  <TableHead style={{ color: 'var(--text-secondary)' }}>Uploader</TableHead>
                  <TableHead style={{ color: 'var(--text-secondary)' }}>Title</TableHead>
                  <TableHead style={{ color: 'var(--text-secondary)' }}>Date</TableHead>
                  <TableHead style={{ color: 'var(--text-secondary)' }}>Tags</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {[...(analytics.uploads ?? [])].reverse().slice(0, 10).map((u, i) => (
                  <TableRow key={i} style={{ borderColor: 'var(--gb-border)' }}>
                    <TableCell>
                      <code className="text-xs" style={{ color: '#3a9e3a' }}>
                        {shortKey(u.pubkey)}
                      </code>
                    </TableCell>
                    <TableCell style={{ color: 'var(--text-primary)' }}>
                      {u.title}
                    </TableCell>
                    <TableCell style={{ color: 'var(--text-secondary)' }}>
                      {fmtDate(u.timestamp)}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {u.tags.slice(0, 3).map(t => (
                          <Badge key={t} variant="secondary" className="text-xs">{t}</Badge>
                        ))}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </div>
  );
}

function ModeBadge({ title, mode }: { title: string; mode: ListMode }) {
  const colors: Record<ListMode, { bg: string; text: string; label: string }> = {
    open: { bg: 'rgba(58,158,58,0.15)', text: '#3a9e3a', label: 'Open' },
    whitelist: { bg: 'rgba(29,161,242,0.15)', text: '#1da1f2', label: 'Whitelist' },
    blacklist: { bg: 'rgba(244,67,54,0.15)', text: '#f44336', label: 'Blacklist' },
  };
  const c = colors[mode];
  return (
    <div className="rounded-lg p-4 border" style={{ borderColor: 'var(--gb-border)' }}>
      <p className="text-xs mb-1" style={{ color: 'var(--text-secondary)' }}>{title}</p>
      <span className="px-2 py-1 rounded-full text-sm font-semibold" style={{ background: c.bg, color: c.text }}>
        {c.label}
      </span>
    </div>
  );
}

function CountCard({ label, count }: { label: string; count: number }) {
  return (
    <div className="rounded-lg p-3 border" style={{ borderColor: 'var(--gb-border)' }}>
      <p className="text-xs mb-1" style={{ color: 'var(--text-secondary)' }}>{label}</p>
      <p className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>{count}</p>
    </div>
  );
}

// ── Filters Tab ───────────────────────────────────────────────────────────────

function FiltersTab() {
  const { config, updateConfig } = useAdminConfig();
  const [newFilter, setNewFilter] = useState({
    type: 'keyword' as ContentFilter['type'],
    value: '',
    action: 'hide' as ContentFilter['action'],
    description: '',
  });
  const [error, setError] = useState('');

  const addFilter = () => {
    if (!newFilter.value.trim()) { setError('Value is required'); return; }
    setError('');
    const filter: ContentFilter = {
      id: `filter-${Date.now()}`,
      type: newFilter.type,
      value: newFilter.value.trim(),
      action: newFilter.action,
      enabled: true,
      createdAt: Math.floor(Date.now() / 1000),
      description: newFilter.description.trim() || undefined,
    };
    updateConfig(c => ({ ...c, contentFilters: [...c.contentFilters, filter] }));
    setNewFilter(f => ({ ...f, value: '', description: '' }));
  };

  const removeFilter = (id: string) => {
    updateConfig(c => ({ ...c, contentFilters: c.contentFilters.filter(f => f.id !== id) }));
  };

  const toggleFilter = (id: string) => {
    updateConfig(c => ({
      ...c,
      contentFilters: c.contentFilters.map(f => f.id === id ? { ...f, enabled: !f.enabled } : f),
    }));
  };

  const typeLabels: Record<ContentFilter['type'], string> = {
    keyword: 'Keyword',
    hashtag: 'Hashtag',
    pubkey: 'Pubkey',
    domain: 'Domain',
  };

  const actionColors: Record<ContentFilter['action'], string> = {
    hide: '#f44336',
    flag: '#f7931a',
  };

  return (
    <div className="space-y-6">
      {/* Add new filter */}
      <div
        className="rounded-xl border p-6 space-y-4"
        style={{ background: 'var(--bg-secondary)', borderColor: 'var(--gb-border)' }}
      >
        <h2 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>
          Add Content Filter
        </h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="space-y-1">
            <Label className="text-xs" style={{ color: 'var(--text-secondary)' }}>Filter Type</Label>
            <Select
              value={newFilter.type}
              onValueChange={v => setNewFilter(f => ({ ...f, type: v as ContentFilter['type'] }))}
            >
              <SelectTrigger style={{ background: 'var(--bg-primary)', borderColor: 'var(--gb-border)', color: 'var(--text-primary)' }}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="keyword">Keyword</SelectItem>
                <SelectItem value="hashtag">Hashtag</SelectItem>
                <SelectItem value="pubkey">Pubkey</SelectItem>
                <SelectItem value="domain">Domain</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs" style={{ color: 'var(--text-secondary)' }}>Value</Label>
            <Input
              value={newFilter.value}
              onChange={e => setNewFilter(f => ({ ...f, value: e.target.value }))}
              placeholder={newFilter.type === 'pubkey' ? 'npub1… or hex' : newFilter.type === 'hashtag' ? 'bitcoin' : newFilter.type === 'domain' ? 'example.com' : 'bad word'}
              style={{ background: 'var(--bg-primary)', borderColor: 'var(--gb-border)', color: 'var(--text-primary)' }}
              onKeyDown={e => e.key === 'Enter' && addFilter()}
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs" style={{ color: 'var(--text-secondary)' }}>Action</Label>
            <Select
              value={newFilter.action}
              onValueChange={v => setNewFilter(f => ({ ...f, action: v as ContentFilter['action'] }))}
            >
              <SelectTrigger style={{ background: 'var(--bg-primary)', borderColor: 'var(--gb-border)', color: 'var(--text-primary)' }}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="hide">Hide</SelectItem>
                <SelectItem value="flag">Flag</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs" style={{ color: 'var(--text-secondary)' }}>Description (optional)</Label>
            <Input
              value={newFilter.description}
              onChange={e => setNewFilter(f => ({ ...f, description: e.target.value }))}
              placeholder="Why this filter exists"
              style={{ background: 'var(--bg-primary)', borderColor: 'var(--gb-border)', color: 'var(--text-primary)' }}
            />
          </div>
        </div>
        {error && <p className="text-sm" style={{ color: '#f44336' }}>{error}</p>}
        <Button onClick={addFilter} className="gap-1.5" style={{ background: '#3a9e3a', color: '#fff' }}>
          <PlusIcon />
          Add Filter
        </Button>
      </div>

      {/* Filter list */}
      <div
        className="rounded-xl border overflow-hidden"
        style={{ background: 'var(--bg-secondary)', borderColor: 'var(--gb-border)' }}
      >
        <div className="px-6 py-4 border-b" style={{ borderColor: 'var(--gb-border)' }}>
          <h2 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>
            Active Filters
            <span className="ml-2 text-sm font-normal" style={{ color: 'var(--text-secondary)' }}>
              ({config.contentFilters.length})
            </span>
          </h2>
        </div>
        {config.contentFilters.length === 0 ? (
          <div className="px-6 py-10 text-center">
            <p style={{ color: 'var(--text-secondary)' }}>No filters configured yet.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow style={{ borderColor: 'var(--gb-border)' }}>
                  <TableHead style={{ color: 'var(--text-secondary)' }}>Enabled</TableHead>
                  <TableHead style={{ color: 'var(--text-secondary)' }}>Type</TableHead>
                  <TableHead style={{ color: 'var(--text-secondary)' }}>Value</TableHead>
                  <TableHead style={{ color: 'var(--text-secondary)' }}>Action</TableHead>
                  <TableHead style={{ color: 'var(--text-secondary)' }}>Description</TableHead>
                  <TableHead style={{ color: 'var(--text-secondary)' }}>Added</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {config.contentFilters.map(f => (
                  <TableRow key={f.id} style={{ borderColor: 'var(--gb-border)', opacity: f.enabled ? 1 : 0.5 }}>
                    <TableCell>
                      <Switch checked={f.enabled} onCheckedChange={() => toggleFilter(f.id)} />
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-xs">{typeLabels[f.type]}</Badge>
                    </TableCell>
                    <TableCell>
                      <code className="text-xs px-1.5 py-0.5 rounded" style={{ background: 'var(--bg-primary)', color: '#3a9e3a' }}>
                        {f.value}
                      </code>
                    </TableCell>
                    <TableCell>
                      <span className="text-xs font-semibold" style={{ color: actionColors[f.action] }}>
                        {f.action.toUpperCase()}
                      </span>
                    </TableCell>
                    <TableCell style={{ color: 'var(--text-secondary)', fontSize: '0.75rem' }}>
                      {f.description ?? '—'}
                    </TableCell>
                    <TableCell style={{ color: 'var(--text-secondary)', fontSize: '0.75rem' }}>
                      {fmtDate(f.createdAt)}
                    </TableCell>
                    <TableCell>
                      <button
                        onClick={() => removeFilter(f.id)}
                        className="p-1.5 rounded transition-colors"
                        style={{ color: '#f44336' }}
                        title="Delete filter"
                      >
                        <TrashIcon />
                      </button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Access Tab ────────────────────────────────────────────────────────────────

function AccessTab() {
  const { config, updateConfig } = useAdminConfig();
  const [uploadInput, setUploadInput] = useState('');
  const [uploadLabel, setUploadLabel] = useState('');
  const [uploadReason, setUploadReason] = useState('');
  const [viewInput, setViewInput] = useState('');
  const [viewLabel, setViewLabel] = useState('');
  const [viewReason, setViewReason] = useState('');
  const [uploadErr, setUploadErr] = useState('');
  const [viewErr, setViewErr] = useState('');

  // ----- Upload list management -----
  const addToUploadList = (list: 'whitelist' | 'blacklist') => {
    const hex = fromNpubOrHex(uploadInput);
    if (!hex) { setUploadErr('Invalid npub or pubkey hex'); return; }
    setUploadErr('');
    const entry: NpubEntry = { pubkey: hex, label: uploadLabel.trim() || undefined, addedAt: Math.floor(Date.now() / 1000), reason: uploadReason.trim() || undefined };
    if (list === 'whitelist') {
      if (config.uploadWhitelist.some(e => e.pubkey === hex)) { setUploadErr('Already in whitelist'); return; }
      updateConfig(c => ({ ...c, uploadWhitelist: [...c.uploadWhitelist, entry] }));
    } else {
      if (config.uploadBlacklist.some(e => e.pubkey === hex)) { setUploadErr('Already in blacklist'); return; }
      updateConfig(c => ({ ...c, uploadBlacklist: [...c.uploadBlacklist, entry] }));
    }
    setUploadInput(''); setUploadLabel(''); setUploadReason('');
  };

  const removeUploadEntry = (list: 'whitelist' | 'blacklist', pubkey: string) => {
    if (list === 'whitelist') updateConfig(c => ({ ...c, uploadWhitelist: c.uploadWhitelist.filter(e => e.pubkey !== pubkey) }));
    else updateConfig(c => ({ ...c, uploadBlacklist: c.uploadBlacklist.filter(e => e.pubkey !== pubkey) }));
  };

  // ----- View list management -----
  const addToViewList = (list: 'whitelist' | 'blacklist') => {
    const hex = fromNpubOrHex(viewInput);
    if (!hex) { setViewErr('Invalid npub or pubkey hex'); return; }
    setViewErr('');
    const entry: NpubEntry = { pubkey: hex, label: viewLabel.trim() || undefined, addedAt: Math.floor(Date.now() / 1000), reason: viewReason.trim() || undefined };
    if (list === 'whitelist') {
      if (config.viewWhitelist.some(e => e.pubkey === hex)) { setViewErr('Already in whitelist'); return; }
      updateConfig(c => ({ ...c, viewWhitelist: [...c.viewWhitelist, entry] }));
    } else {
      if (config.viewBlacklist.some(e => e.pubkey === hex)) { setViewErr('Already in blacklist'); return; }
      updateConfig(c => ({ ...c, viewBlacklist: [...c.viewBlacklist, entry] }));
    }
    setViewInput(''); setViewLabel(''); setViewReason('');
  };

  const removeViewEntry = (list: 'whitelist' | 'blacklist', pubkey: string) => {
    if (list === 'whitelist') updateConfig(c => ({ ...c, viewWhitelist: c.viewWhitelist.filter(e => e.pubkey !== pubkey) }));
    else updateConfig(c => ({ ...c, viewBlacklist: c.viewBlacklist.filter(e => e.pubkey !== pubkey) }));
  };

  return (
    <div className="space-y-8">

      {/* ── Upload Access ── */}
      <Section title="Upload Access Control" subtitle="Control who can upload videos to Goblinbox">
        {/* Mode selector */}
        <ModeSelector
          label="Upload Mode"
          mode={config.uploadMode}
          onChange={v => updateConfig(c => ({ ...c, uploadMode: v }))}
          descriptions={{
            open: 'Anyone can upload (blacklist still applies)',
            whitelist: 'Only whitelisted npubs may upload',
            blacklist: 'Anyone except blacklisted npubs may upload',
          }}
        />

        {/* Add npub */}
        <NpubEntryForm
          inputValue={uploadInput}
          labelValue={uploadLabel}
          reasonValue={uploadReason}
          error={uploadErr}
          onInputChange={setUploadInput}
          onLabelChange={setUploadLabel}
          onReasonChange={setUploadReason}
          onAddWhitelist={() => addToUploadList('whitelist')}
          onAddBlacklist={() => addToUploadList('blacklist')}
        />

        {/* Lists */}
        <div className="grid lg:grid-cols-2 gap-4">
          <NpubList
            title="Upload Whitelist"
            entries={config.uploadWhitelist}
            color="#1da1f2"
            onRemove={pk => removeUploadEntry('whitelist', pk)}
          />
          <NpubList
            title="Upload Blacklist"
            entries={config.uploadBlacklist}
            color="#f44336"
            onRemove={pk => removeUploadEntry('blacklist', pk)}
          />
        </div>
      </Section>

      {/* ── View Access ── */}
      <Section title="View Access Control" subtitle="Control whose videos appear in feeds and searches">
        <ModeSelector
          label="View Mode"
          mode={config.viewMode}
          onChange={v => updateConfig(c => ({ ...c, viewMode: v }))}
          descriptions={{
            open: 'Everyone\'s videos are visible (blacklist still applies)',
            whitelist: 'Only videos from whitelisted npubs are shown',
            blacklist: 'All videos except blacklisted npubs are shown',
          }}
        />

        <NpubEntryForm
          inputValue={viewInput}
          labelValue={viewLabel}
          reasonValue={viewReason}
          error={viewErr}
          onInputChange={setViewInput}
          onLabelChange={setViewLabel}
          onReasonChange={setViewReason}
          onAddWhitelist={() => addToViewList('whitelist')}
          onAddBlacklist={() => addToViewList('blacklist')}
        />

        <div className="grid lg:grid-cols-2 gap-4">
          <NpubList
            title="View Whitelist"
            entries={config.viewWhitelist}
            color="#1da1f2"
            onRemove={pk => removeViewEntry('whitelist', pk)}
          />
          <NpubList
            title="View Blacklist"
            entries={config.viewBlacklist}
            color="#f44336"
            onRemove={pk => removeViewEntry('blacklist', pk)}
          />
        </div>
      </Section>
    </div>
  );
}

// ── Analytics Tab ─────────────────────────────────────────────────────────────

function AnalyticsTab() {
  const { analytics } = useAdminAnalytics();
  const [range, setRange] = useState<'7d' | '30d' | 'all'>('7d');

  const now = Math.floor(Date.now() / 1000);
  const cutoff = range === '7d' ? now - 7 * 86400 : range === '30d' ? now - 30 * 86400 : 0;

  const views = (analytics.pageViews ?? []).filter(t => t > cutoff);
  const uploads = (analytics.uploads ?? []).filter(u => u.timestamp > cutoff);
  const searches = (analytics.searchQueries ?? []).filter(s => s.timestamp > cutoff);

  // Group page views by day
  const viewsByDay = useMemo(() => {
    const map: Record<string, number> = {};
    views.forEach(t => {
      const d = new Date(t * 1000).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      map[d] = (map[d] ?? 0) + 1;
    });
    return Object.entries(map).slice(-14);
  }, [views]);

  // Top uploaders
  const uploaderCounts = useMemo(() => {
    const map: Record<string, number> = {};
    uploads.forEach(u => { map[u.pubkey] = (map[u.pubkey] ?? 0) + 1; });
    return Object.entries(map).sort(([, a], [, b]) => b - a).slice(0, 10);
  }, [uploads]);

  // Top search queries
  const topSearches = useMemo(() => {
    const map: Record<string, number> = {};
    searches.forEach(s => { map[s.query] = (map[s.query] ?? 0) + 1; });
    return Object.entries(map).sort(([, a], [, b]) => b - a).slice(0, 10);
  }, [searches]);

  const maxViews = Math.max(...viewsByDay.map(([, v]) => v), 1);

  return (
    <div className="space-y-6">
      {/* Range selector */}
      <div className="flex items-center gap-3">
        <p className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>Time Range:</p>
        {(['7d', '30d', 'all'] as const).map(r => (
          <button
            key={r}
            onClick={() => setRange(r)}
            className={cn(
              'px-3 py-1 rounded-full text-sm font-medium transition-colors',
            )}
            style={{
              background: range === r ? '#3a9e3a' : 'var(--bg-secondary)',
              color: range === r ? '#fff' : 'var(--text-secondary)',
              border: `1px solid ${range === r ? '#3a9e3a' : 'var(--gb-border)'}`,
            }}
          >
            {r === 'all' ? 'All time' : r}
          </button>
        ))}
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { label: 'Page Views', value: views.length, color: '#3a9e3a' },
          { label: 'Uploads', value: uploads.length, color: '#f7931a' },
          { label: 'Searches', value: searches.length, color: '#1da1f2' },
        ].map(s => (
          <div key={s.label} className="rounded-xl p-5 border" style={{ background: 'var(--bg-secondary)', borderColor: 'var(--gb-border)' }}>
            <p className="text-sm mb-1" style={{ color: 'var(--text-secondary)' }}>{s.label}</p>
            <p className="text-3xl font-bold" style={{ color: s.color }}>{s.value.toLocaleString()}</p>
          </div>
        ))}
      </div>

      {/* Page views chart */}
      <div className="rounded-xl border p-6 space-y-4" style={{ background: 'var(--bg-secondary)', borderColor: 'var(--gb-border)' }}>
        <h2 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>
          Page Views Over Time
        </h2>
        {viewsByDay.length === 0 ? (
          <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>No data for this period.</p>
        ) : (
          <div className="flex items-end gap-1.5 h-32 overflow-x-auto pb-2">
            {viewsByDay.map(([day, count]) => (
              <div key={day} className="flex flex-col items-center gap-1 flex-shrink-0" style={{ minWidth: 32 }}>
                <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>{count}</span>
                <div
                  className="w-6 rounded-t transition-all"
                  style={{
                    height: `${Math.max(4, (count / maxViews) * 96)}px`,
                    background: 'linear-gradient(to top, #3a9e3a, #5dc55d)',
                  }}
                />
                <span className="text-xs text-center leading-tight" style={{ color: 'var(--text-secondary)', fontSize: '0.6rem' }}>
                  {day}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Top uploaders + top searches */}
      <div className="grid lg:grid-cols-2 gap-4">
        <div className="rounded-xl border p-6 space-y-3" style={{ background: 'var(--bg-secondary)', borderColor: 'var(--gb-border)' }}>
          <h2 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>Top Uploaders</h2>
          {uploaderCounts.length === 0 ? (
            <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>No uploads in this period.</p>
          ) : (
            <div className="space-y-2">
              {uploaderCounts.map(([pk, count]) => (
                <div key={pk} className="flex items-center justify-between py-1.5">
                  <code className="text-xs" style={{ color: '#3a9e3a' }}>{shortKey(pk)}</code>
                  <Badge variant="outline">{count} upload{count !== 1 ? 's' : ''}</Badge>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="rounded-xl border p-6 space-y-3" style={{ background: 'var(--bg-secondary)', borderColor: 'var(--gb-border)' }}>
          <h2 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>Top Searches</h2>
          {topSearches.length === 0 ? (
            <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>No searches in this period.</p>
          ) : (
            <div className="space-y-2">
              {topSearches.map(([q, count]) => (
                <div key={q} className="flex items-center justify-between py-1.5">
                  <span className="text-sm" style={{ color: 'var(--text-primary)' }}>"{q}"</span>
                  <Badge variant="outline">{count}×</Badge>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Unique visitors */}
      <div className="rounded-xl border p-6 space-y-3" style={{ background: 'var(--bg-secondary)', borderColor: 'var(--gb-border)' }}>
        <h2 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>
          Unique Visitors
          <span className="ml-2 text-sm font-normal" style={{ color: 'var(--text-secondary)' }}>
            (all time — {analytics.uniqueVisitors?.length ?? 0} logged-in users)
          </span>
        </h2>
        {(analytics.uniqueVisitors?.length ?? 0) === 0 ? (
          <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>No visitor data yet.</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {(analytics.uniqueVisitors ?? []).slice(0, 30).map(pk => (
              <code key={pk} className="text-xs px-2 py-1 rounded" style={{ background: 'var(--bg-primary)', color: '#3a9e3a' }}>
                {shortKey(pk)}
              </code>
            ))}
            {(analytics.uniqueVisitors?.length ?? 0) > 30 && (
              <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                +{(analytics.uniqueVisitors?.length ?? 0) - 30} more
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Shared sub-components ─────────────────────────────────────────────────────

function Section({ title, subtitle, children }: { title: string; subtitle?: string; children: React.ReactNode }) {
  return (
    <div className="rounded-xl border p-6 space-y-5" style={{ background: 'var(--bg-secondary)', borderColor: 'var(--gb-border)' }}>
      <div>
        <h2 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>{title}</h2>
        {subtitle && <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{subtitle}</p>}
      </div>
      {children}
    </div>
  );
}

function ModeSelector({
  label,
  mode,
  onChange,
  descriptions,
}: {
  label: string;
  mode: ListMode;
  onChange: (v: ListMode) => void;
  descriptions: Record<ListMode, string>;
}) {
  const modes: { value: ListMode; label: string; color: string }[] = [
    { value: 'open', label: 'Open', color: '#3a9e3a' },
    { value: 'whitelist', label: 'Whitelist', color: '#1da1f2' },
    { value: 'blacklist', label: 'Blacklist', color: '#f44336' },
  ];

  return (
    <div className="space-y-3">
      <Label style={{ color: 'var(--text-primary)' }}>{label}</Label>
      <div className="flex flex-wrap gap-2">
        {modes.map(m => (
          <button
            key={m.value}
            onClick={() => onChange(m.value)}
            className="px-4 py-2 rounded-lg text-sm font-medium transition-all border"
            style={{
              background: mode === m.value ? m.color : 'var(--bg-primary)',
              color: mode === m.value ? '#fff' : 'var(--text-secondary)',
              borderColor: mode === m.value ? m.color : 'var(--gb-border)',
            }}
          >
            {m.label}
          </button>
        ))}
      </div>
      <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>
        {descriptions[mode]}
      </p>
    </div>
  );
}

function NpubEntryForm({
  inputValue,
  labelValue,
  reasonValue,
  error,
  onInputChange,
  onLabelChange,
  onReasonChange,
  onAddWhitelist,
  onAddBlacklist,
}: {
  inputValue: string;
  labelValue: string;
  reasonValue: string;
  error: string;
  onInputChange: (v: string) => void;
  onLabelChange: (v: string) => void;
  onReasonChange: (v: string) => void;
  onAddWhitelist: () => void;
  onAddBlacklist: () => void;
}) {
  return (
    <div className="space-y-3 pt-2 border-t" style={{ borderColor: 'var(--gb-border)' }}>
      <p className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>Add npub to list</p>
      <div className="grid sm:grid-cols-3 gap-3">
        <div className="space-y-1">
          <Label className="text-xs" style={{ color: 'var(--text-secondary)' }}>npub or hex pubkey</Label>
          <Input
            value={inputValue}
            onChange={e => onInputChange(e.target.value)}
            placeholder="npub1… or hex"
            style={{ background: 'var(--bg-primary)', borderColor: 'var(--gb-border)', color: 'var(--text-primary)' }}
          />
        </div>
        <div className="space-y-1">
          <Label className="text-xs" style={{ color: 'var(--text-secondary)' }}>Label (optional)</Label>
          <Input
            value={labelValue}
            onChange={e => onLabelChange(e.target.value)}
            placeholder="e.g. Bob's account"
            style={{ background: 'var(--bg-primary)', borderColor: 'var(--gb-border)', color: 'var(--text-primary)' }}
          />
        </div>
        <div className="space-y-1">
          <Label className="text-xs" style={{ color: 'var(--text-secondary)' }}>Reason (optional)</Label>
          <Input
            value={reasonValue}
            onChange={e => onReasonChange(e.target.value)}
            placeholder="Reason for this entry"
            style={{ background: 'var(--bg-primary)', borderColor: 'var(--gb-border)', color: 'var(--text-primary)' }}
          />
        </div>
      </div>
      {error && <p className="text-xs" style={{ color: '#f44336' }}>{error}</p>}
      <div className="flex gap-2">
        <Button size="sm" onClick={onAddWhitelist} style={{ background: '#1da1f2', color: '#fff' }} className="gap-1.5">
          <PlusIcon />+ Whitelist
        </Button>
        <Button size="sm" onClick={onAddBlacklist} style={{ background: '#f44336', color: '#fff' }} className="gap-1.5">
          <PlusIcon />+ Blacklist
        </Button>
      </div>
    </div>
  );
}

function NpubList({
  title,
  entries,
  color,
  onRemove,
}: {
  title: string;
  entries: NpubEntry[];
  color: string;
  onRemove: (pubkey: string) => void;
}) {
  return (
    <div className="rounded-lg border overflow-hidden" style={{ borderColor: 'var(--gb-border)' }}>
      <div className="px-4 py-3 border-b flex items-center justify-between" style={{ borderColor: 'var(--gb-border)', background: 'var(--bg-primary)' }}>
        <span className="text-sm font-semibold" style={{ color }}>{title}</span>
        <Badge variant="outline" style={{ borderColor: color, color }}>{entries.length}</Badge>
      </div>
      {entries.length === 0 ? (
        <div className="px-4 py-6 text-center text-sm" style={{ color: 'var(--text-secondary)' }}>
          Empty
        </div>
      ) : (
        <div className="divide-y max-h-64 overflow-y-auto" style={{ borderColor: 'var(--gb-border)' }}>
          {entries.map(e => (
            <div key={e.pubkey} className="flex items-center justify-between px-4 py-2.5 gap-2">
              <div className="min-w-0">
                {e.label && (
                  <p className="text-xs font-medium truncate" style={{ color: 'var(--text-primary)' }}>{e.label}</p>
                )}
                <code className="text-xs" style={{ color: 'var(--text-secondary)' }}>{shortKey(e.pubkey)}</code>
                {e.reason && (
                  <p className="text-xs truncate" style={{ color: 'var(--text-secondary)' }}>— {e.reason}</p>
                )}
              </div>
              <button
                onClick={() => onRemove(e.pubkey)}
                className="shrink-0 p-1 rounded transition-colors"
                style={{ color: '#f44336' }}
                title="Remove"
              >
                <TrashIcon />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
